import { GridTypes, MappingDisplayType, TextBlockCategory } from "Enums";

import { IBaseGrid } from "Grids/Interfaces";
import { IControl } from "Controls/Interfaces";
import { IDMSElement, IStringDictionary } from "Core/Interfaces";
import { IControlDefinition, IFilter } from "Grids/Interfaces";

import { Menu, QueryParameter, ApplicationParameter, CustomBinding, Navigation, Mapping } from "Core";
import { ITextBlockArray, IEnvironment, IBaseResult } from "Entities";

import { Configuration } from "Configuration";
import { ServiceProvider } from "Service";
import jstz = require("jstimezonedetect");

/**
 * ## Context
 *
 * Entry point for dms application.
 *
 * Provides functions to initialize grids and mappings.
 * @since 4.0.0
 */
export class CoreContext {
    /**
     * @hidden
     */
    // protected _grids: Array<IBaseGrid>;

    /**
     * @hidden
     */
    // protected containerGrids: Array<IBaseGrid>;

    /**
     * @hidden
     */
    protected _environment: IEnvironment;

    /**
     * @hidden
     */
    protected _pageViewModel: kendo.data.ObservableObject;

    /**
     * @hidden
     */
    protected _textBlocks: ITextBlockArray;

    /**
     * @hidden
     */
    protected _pageToken: string;

    /**
     * @hidden
     */
    public mappingName: string;

    /**
     * @hidden
     */
    public initialTextBlockCategory: string;

    /**
     * @hidden
     */
    protected _currentTextBlockCategory: string;

    /**
     * @hidden
     */
    public static serviceProviderInstance: ServiceProvider = new ServiceProvider();

    /**
     * @hidden
     */
    public serviceProvider: ServiceProvider;

    /**
     * @hidden
     */
    protected static _context: CoreContext;

    /**
     * @hidden
     */
    constructor() {
        this.serviceProvider = CoreContext.serviceProviderInstance;

        // create viewModel
        this._pageViewModel = kendo.observable({
            TextBlock: {},
            getTextBlock: (textBlockName: string) => {
                return this.serviceProvider.TextBlock.get(textBlockName);
            },
            getUserDataTextBlock: (textBlockName: string) => {
                return this.serviceProvider.TextBlock.get(textBlockName, "userdata");
            }
        });
    }

    public removeAdditionalMapping(mapping: Mapping): void {
        let idx = this.additionalMappings.indexOf(mapping);
        if (idx > -1) {
            this.additionalMappings.splice(idx, 1);
        }
    }

    /**
     * Get all grids registered on CoreContext.
     * @hidden
     */
    public getGrids(): IBaseGrid[] {
        let mappings = this.getMappings();
        let grids: IBaseGrid[] = [];
        for (let i = 0, len = mappings.length; i < len; i++) {
            grids = grids.concat(mappings[i].getGrids());
        }
        if (this.menuMapping) {
            grids = grids.concat(this.menuMapping.getGrids());
        }
        return grids;
    }

    public getMappings(): Mapping[] {
        let mappings: Mapping[] = [];
        if (this.mapping) {
            mappings.push(this.mapping);
        }
        if (this.menuMapping) {
            mappings.push(this.menuMapping);
        }
        mappings = mappings.concat(this.additionalMappings);
        return mappings;
    }

    /**
     * Get first Filter
     * @hidden
     */
    public getFilter(): IBaseGrid {
        let mappings = this.getMappings();
        for (let i = 0, len = mappings.length; i < len; i++) {
            let grids = mappings[i].getGrids();
            for (let j = 0, gridLen = grids.length; j < gridLen; j++) {
                if (grids[j].getGridType() == GridTypes.Filter) {
                    return grids[j];
                }
            }
        }
        return null;
    }

    /**
     * Gets the pageViewModel which holds TextBlock etc.
     * @hidden
     */
    public get pageViewModel() {
        return this._pageViewModel;
    }

    // /**
    //  * Gets the textblocks loaded with mappingName / category
    //  */
    // public get textBlocks() {
    //     return this._textBlocks;
    // }

    /**
     * Gets the unique page token
     * @hidden
     */
    public get pageToken() {
        return this._pageToken;
    }

    /**
     * @hidden
     */
    public get currentTextBlockCategory() {
        return this._currentTextBlockCategory;
    }

    /**
     * @hidden
     */
    public set currentTextBlockCategory(val: string) {
        this._currentTextBlockCategory = val;
    }

    protected globalInitializePromise: Promise<void>;

    /**
     * Initialize CoreContext.
     * @param pageToken Unique token which is processed with all requests
     * @param puiTimeout PuiTimeout.
     * @param textBlockCategory Name of the textBlockCategory.
     * @hidden
     */
    public initialize(pageToken: string, puiTimeout: number, textBlockCategory: string): void {
        this._pageToken = pageToken;
        kendo.bind($(".dms-content-container"), this._pageViewModel);
        QueryParameter.initialize();
        this.globalInitializePromise = this.initializeMenuMapping().then(() => {
            Menu.initialize(this.serviceProvider.Menu, this.serviceProvider.TextBlock);
        });
        CustomBinding.initialize();
        Navigation.initialize();
        textBlockCategory = QueryParameter.get("tbm") || textBlockCategory;
        let textBlockCategories = ["userdata", TextBlockCategory.global, textBlockCategory];
        this.serviceProvider.TextBlock.load(this._environment.LanguageCode, textBlockCategories.toString()).then(textblocks => {
            document.title =
                this._environment.BrowserTitle +
                " " +
                this.serviceProvider.TextBlock.getChecked(textblocks[textBlockCategory], "pageTitle", textBlockCategory, true);
            this._pageViewModel.set("TextBlock", textblocks);
        });
        this.initialTextBlockCategory = this._currentTextBlockCategory = textBlockCategory;

        this.serviceProvider.PageToken.startPolling(this._pageToken, puiTimeout);
        $(window).on("beforeunload", () => this.onWindowUnload());
    }

    /**
     * @hidden
     */
    public allowPageTokenRelease = true;

    private onWindowUnload() {
        try {
            if (this.allowPageTokenRelease) {
                this.serviceProvider.PageToken.sendRelease();
            }
        } catch (e) {}
    }

    /**
     * Configures the Context with the Environment.
     * @param environment Environment.
     * @hidden
     */
    public configure(environment: IEnvironment): void {
        this._environment = environment;

        // initialize both cultures
        kendo.culture("de");
        kendo.culture("en-US");

        if (environment.Culture) {
            kendo.culture(environment.Culture);
            kendo.cultures["current"].numberFormat[","] = environment.NumberFormatGroupSeparator;
            kendo.cultures["current"].numberFormat["."] = environment.NumberFormatCommaSeparator;
        }
        // if (!environment.ClientTimeZone) {
            let tz = jstz.determine();
            environment.ClientTimeZone = tz.name();
        // }
    }

    /**
     * Set the application parameters for this instance.
     * @param parameters
     * @hidden
     */
    public initializeApplicationParameters(parameters: IStringDictionary<string>) {
        if (parameters) {
            ApplicationParameter.initialize(parameters);
        }
    }

    protected mapping: Mapping;

    /**
     * Initializes a mapping on this page.
     * @param mappingName Name of the mapping.
     * @hidden
     */
    public async initializeMapping(
        mappingName: string,
        displayType: MappingDisplayType = MappingDisplayType.FilterAndGrids
    ): Promise<void> {
        this.mappingName = mappingName;
        this.mapping = new Mapping(mappingName, this._currentTextBlockCategory, this.serviceProvider, this.globalInitializePromise);
        // this.serviceProvider.TextBlock.getAll().then(() => { this.resizePage(); });
        await this.mapping.initialize($(".dms-mapping-wrapper." + mappingName), displayType);
        this.resizePage();

        if (this._environment.AutoCreateTextBlocksIfMissing) {
            setTimeout(() => this.serviceProvider.TextBlock.uploadMissingTextBlocks(), 2000);
        }
        $(window).resize(() => this.resizePage());
    }

    protected menuMapping: Mapping;

    public async initializeMenuMapping(): Promise<void> {
        if (this._environment.MenuMappingName && $(".dms-sidemenu-mapping").length == 1) {
            this.menuMapping = new Mapping(this._environment.MenuMappingName, TextBlockCategory.global, this.serviceProvider, undefined);
            await this.menuMapping.initialize($(".dms-sidemenu-mapping"));
        }
    }

    private additionalMappings: Mapping[] = [];

    /**
     * Initialize a mapping in given container.
     * @param container The container in which the mapping will be initialized.
     * @param mappingName Name of the mapping to be initialized. The TextBlocks for the mapping are loaded.
     * @param displayType Specify if both filters and grids should be shown or just one of them.
     * @hidden
     */
    public async initializeMappingInContainer(
        container: JQuery,
        mappingName: string,
        displayType: MappingDisplayType = MappingDisplayType.FilterAndGrids
    ): Promise<Mapping> {
        let textBlocks = await this.serviceProvider.TextBlock.load(this._environment.LanguageCode, mappingName);

        let textBlock = this._pageViewModel.get("TextBlock");
        $.extend(textBlock, textBlocks);
        this._pageViewModel.set("TextBlock", textBlock);
        let mapping = new Mapping(mappingName, mappingName, this.serviceProvider, this.globalInitializePromise);
        this.additionalMappings.push(mapping);
        await mapping.initialize(container, displayType);
        // await this.initializeGrids(container, mappingName, grids, displayType);
        // this.containerGrids = this.containerGrids.concat(grids);
        // return grids;

        if (this._environment.AutoCreateTextBlocksIfMissing) {
            setTimeout(() => this.serviceProvider.TextBlock.uploadMissingTextBlocks(), 2000);
        }

        return mapping;
    }

    /**
     * Triggers the first default button on the current page.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Context.triggerDefaultButton();
     * ```
     */
    public triggerDefaultButton(): void {
        // let mappings = this.getMappings();
        // for (let i = 0, len = mappings.length; i < len; i++) {
        //     if (mappings[i].triggerDefaultButton()) {
        //         break;
        //     }
        // }
        if (this.mapping) {
            this.mapping.triggerDefaultButton();
        }
    }

    /**
     * Initialize a custom page.
     * @hidden
     */
    public initializePage(): void {}

    /**
     * Hides all registered grids.
     * @param clearGrid - True if the grids should be cleared aswell.
     * @hidden
     */
    public hideGrids(clearGrid: boolean = true): void {
        if (this.mapping) {
            let grids = this.mapping.getGrids();
            for (let i = 0, len = grids.length; i < len; i++) {
                let grid = grids[i];
                if (grid.getGridType() != GridTypes.Filter) {
                    grid.hide();
                    if (clearGrid) {
                        grid.clear();
                    }
                }
            }
        }
    }

    /**
     * Initializes control based on IControlDefinition and Configuration.
     * @param controlDefinition
     * @param textBlockCategory
     * @hidden
     */
    public initializeControl(controlDefinition: IControlDefinition, textBlockCategory: string): IControl {
        let controlTypeFactory = Configuration.getControlFactory(controlDefinition.ControlTypeID);
        if (controlTypeFactory != null) {
            return controlTypeFactory(controlDefinition, textBlockCategory, this.serviceProvider);
        }
        console.error("factory not found");
        return null;
    }

    /**
     * Validates Selector for JQuery-Element.
     * @param selector
     * @hidden
     */
    public validateElement(selector: string | JQuery): JQuery {
        if (typeof selector === "string") {
            return $(selector);
        } else if (selector instanceof jQuery) {
            // JQuery
            return selector;
        } else {
            throw new Error("Invalid jQuery selector!");
        }
    }

    /**
     * Calculate the available height for the content area.
     * @hidden
     */
    public getAvailableHeight(): number {
        let positionTop = 0;
        let windowHeight = $(window).height();

        if (this.mapping && this.mapping.getGrids().length > 0) {
            positionTop = this.mapping
                .getGrids()[0]
                .getOuterElement()
                .offset().top;
        }
        return windowHeight - positionTop;
    }

    /**
     * Resize grids in available height.
     * @param grids Grids which should be resized.
     * @param availableHeight Available height in pixel.
     * @hidden
     */
    protected resizeGrids(grids: IBaseGrid[], availableHeight: number) {
        let gridCount = 0;
        let filterHeight = 0;
        for (let i = 0, len = grids.length; i < len; i++) {
            let grid = grids[i];

            if (grid.isVisible()) {
                if (grid.isAutoResize) {
                    gridCount++;
                } else {
                    filterHeight += $(grid.getElement())
                        .parent(".dms-outer-element")
                        .outerHeight(true);
                }
            }
        }

        if (gridCount > 0) {
            let availableGridHeight = availableHeight - filterHeight;

            let gridHeight = availableGridHeight > 250 ? availableGridHeight / gridCount - 2 : 300; // -2 for border on kendo Grid
            for (let i = 0, len = grids.length; i < len; i++) {
                let grid = grids[i];
                if (grid.isAutoResize && grid.isVisible()) {
                    grid.setHeight(gridHeight);
                }
            }
        }
    }

    /**
     * Resizes Grids which have no height defined.
     * @hidden
     */
    public resizePage(): void {
        let availableHeight = this.getAvailableHeight();
        if (this.mapping) {
            this.mapping.setHeight(availableHeight);
        }
        // this.resizeGrids(this._grids, availableHeight);
    }

    /**
     * Static wrapper for initialize on Instance.
     * @param pageToken Unique token which is processed with all requests
     * @param puiTimeout PuiTimeout.
     * @param textBlockCategory Name of the textBlockCategory.
     * @hidden
     */
    public static initialize(pageToken: string, puiTimeout: number, textBlockCategory: string): void {
        this.Instance.initialize(pageToken, puiTimeout, textBlockCategory);
    }

    /**
     * Static wrapper for initializeMapping on Instance.
     * @param mappingName
     * @hidden
     */
    public static initializeMapping(mappingName: string): Promise<void> {
        return this.Instance.initializeMapping(mappingName);
    }

    /**
     * Static wrapper for initialize on Instance.
     * @param textBlockCategory
     * @hidden
     */
    public static initializePage(): void {
        this.Instance.initializePage();
    }

    /**
     * Static wrapper for configure on Instance.
     * @param environment
     * @hidden
     */
    public static configure(environment: IEnvironment): void {
        this.Instance.configure(environment);
    }

    /**
     * Static wrapper for initializeApplicationParameters on Instance.
     * @param parameters
     * @hidden
     */
    public static initializeApplicationParameters(parameters: IStringDictionary<any>) {
        this.Instance.initializeApplicationParameters(parameters);
    }

    /**
     * Get DMS-Environment (culture, settings).
     * @hidden
     */
    public static get Environment(): IEnvironment {
        return this.Instance._environment;
    }

    /**
     * Get/Create Instance of CoreContext.
     * @hidden
     */
    public static get Instance(): CoreContext {
        return this._context == null ? (this._context = new CoreContext()) : this._context;
    }

    /**
     * @hidden
     */
    public static set Instance(instance: CoreContext) {
        this._context = instance;
    }

    /**
     * Reloads all grids on the current page.
     * @hidden
     */
    public reloadGrids(reloadFilters: boolean) {
        let grids = this.getGrids();
        for (let i = 0, len = grids.length; i < len; i++) {
            let grid = grids[i];
            if (grid.getGridType() != GridTypes.Filter || reloadFilters) {
                grid.reload();
            }
        }
    }

    /**
     * @hidden
     * @param result
     */
    public static handleServiceError(result: any) {
        if (result) {
            this.Instance.serviceProvider.PageToken.stopPolling();

            // user logged out and is unauthorized now.
            if (result.status == 401) {
                Navigation.errorAsync(
                    this.serviceProviderInstance.TextBlock.get("SessionExpired", TextBlockCategory.global),
                    "Session"
                ).then(() => {
                    this.serviceProviderInstance.Menu.clearCache();
                    Navigation.navigate("~/Account/LogoffGet");
                });
            }
        }
    }

    /**
     * @hidden
     * @param result
     */
    public static handleServiceResult<T extends IBaseResult>(result: T): T {
        if (result && result.errorTextBlockName) {
            Navigation.errorAsync(
                this.serviceProviderInstance.TextBlock.get(result.errorTextBlockName, this.Instance._currentTextBlockCategory),
                "Error"
            );
            console.log(result.errorDetail);
        } else if (result && result.error) {
            Navigation.errorAsync(result.error + "<br/><br/>Error ID: " + result.errorId, "Error");
            console.log(result.errorDetail);
        }

        if (result && result.isSessionExpired) {
            this.Instance.serviceProvider.PageToken.stopPolling();
            Navigation.errorAsync(this.serviceProviderInstance.TextBlock.get("SessionExpired", TextBlockCategory.global), "Session").then(
                () => {
                    this.serviceProviderInstance.Menu.clearCache();
                    Navigation.navigate("~/Account/LogoffGet");
                }
            );
            return result;
        }

        return result;
    }

    /**
     * @hidden
     */
    public static getFilterParameters(): { [key: string]: any } {
        let filterparams: { [key: string]: any } = {};
        let grids = this.Instance.getGrids();
        for (let i = 0, len = grids.length; i < len; i++) {
            let grid = grids[i];
            if (grid.getGridType() == GridTypes.Filter) {
                let filter = <IFilter>grid;
                if (filter.getData) {
                    let filterdata = filter.getData();
                    filterparams = $.extend(filterparams, filterparams, filterdata);
                }
            }
        }
        return filterparams;
    }

    /**
     * @hidden
     */
    public static addTextBlocks(textBlocks: ITextBlockArray, textBlockCategory: string) {
        let existingTextBlocks = this.Instance.pageViewModel.get("TextBlock");
        existingTextBlocks[textBlockCategory] = textBlocks;
        this.Instance.pageViewModel.set("TextBlock", existingTextBlocks);
    }
}

/**
 * Reloads all grids on the current page.
 * @param reloadFilters Pass true if the filter should be reloaded aswell.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * DMS.reloadGrids();
 *
 * // reload filter aswell
 * DMS.reloadGrids(true);
 * ```
 */
export function reloadGrids(reloadFilters: boolean = false): void {
    CoreContext.Instance.reloadGrids(reloadFilters);
}

/**
 * Check this variable in scripts to distinguish between new JavaScript and legacy code.
 *
 * This is useful while porting scripts to the new version without breaking the page in 3.7.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * if(DMS.isV4) {
 *     alert('hello in v4');
 * } else {
 *     alert('hello in v3');
 * }
 * ```
 */
export const isV4 = true;

/**
 * Hides all grids on the current page.
 *
 * @attention Filters stay visible.
 * @param clearGrids Pass true if the grids should be cleared aswell.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * DMS.hideGrids();
 * DMS.hideGrids(true);
 * ```
 */
export function hideGrids(clearGrids: boolean = true): void {
    CoreContext.Instance.hideGrids(clearGrids);
}

/**
 * Initialize a mapping in given specific jQuery-container.
 * @param container The jQuery-container in which the mapping will be initialized.
 * @param mappingName Name of the mapping to be initialized. The TextBlocks for the mapping are loaded.
 * @param displayType Specify if both filters and grids should be shown or just one of them.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * var container = $(".container");
 * DMS.initializeMappingInContainer(container, "sec_User");
 *
 * // only grids
 * var container = $(".container");
 * DMS.initializeMappingInContainer(container, "sec_User", DMS.Enums.MappingDisplayType.GridsOnly);
 * ```
 */
export async function initializeMappingInContainer(
    container: JQuery,
    mappingName: string,
    displayType: MappingDisplayType = MappingDisplayType.FilterAndGrids
): Promise<void> {
    await CoreContext.Instance.initializeMappingInContainer(container, mappingName, displayType);
}

/**
 * Selector to find DMS Controls or DMS Grids on the current page.
 * @param selector Either string or JQuery-selector or ControlID.
 * @since 4.0.0
 * @returns Either the respective control instance, grid instance or null if no element was found.
 * @example
 * ```JavaScript
 * $dms(1337); // returns control instance with the id 1337.
 * $dms("tst_my_Grid"); // returns grid instance of the grid with the name tst_my_Grid.
 * $dms(".dms-grid"); // returns grid instance of first grid on this page.
 * ```
 */
export function $dms(selector: string | JQuery | HTMLElement | number): IDMSElement {
    if (selector != null) {
        if (typeof selector === "string") {
            // String
            let gridName = selector;

            let grids = CoreContext.Instance.getGrids();
            for (let i = 0, len = grids.length; i < len; i++) {
                if (grids[i].getGridName() == gridName) {
                    return grids[i];
                }
            }
            return $dms($(selector)[0]);
        } else if (typeof selector === "number") {
            let controlId = selector;

            let grids = CoreContext.Instance.getGrids();
            for (let i = 0, len = grids.length; i < len; i++) {
                let control = grids[i].findControl(controlId);
                if (control != null) {
                    return control;
                }
            }
            return null;
        } else if ((<JQuery>selector).jquery && (<JQuery>selector).length) {
            // JQuery
            let jquerySelector = <JQuery>selector;
            if (jquerySelector.length > 0) {
                return $dms(jquerySelector[0]);
            }
        } else {
            // HTMLElement
            let domSelector: HTMLElement = <HTMLElement>selector;

            let grids = CoreContext.Instance.getGrids();
            let gridsArrLength = grids.length;

            for (let i = 0, len = grids.length; i < len; i++) {
                let control = grids[i].findControl(domSelector);
                if (control != null) {
                    return control;
                }
            }

            // loop through all grids.
            for (let i = 0; i < gridsArrLength; i++) {
                if (grids[i].getElement() == domSelector) {
                    // compare dom elements
                    return grids[i];
                }
            }
        }
    }
    return null;
}
